import { type RefObject, useEffect } from 'react'

type Handler = () => unknown

type Options = {
  enabledAction: boolean
}

export const useInfiniteScroll = <T extends HTMLElement = HTMLElement>(
  ref: RefObject<T>,
  hasNextPage: boolean | undefined,
  handler: Handler,
  triggerGap: number | undefined = 10,
  options: Options = {
    enabledAction: true
  }
) => {
  useEffect(() => {
    const element = ref.current

    if (!element) {
      return
    }

    const handleElementScroll = (event: Event) => {
      const { clientHeight, scrollHeight, scrollTop } =
        event.target as T
      const isPageBottomAlmostReached =
        scrollTop + clientHeight >= scrollHeight - triggerGap

      if (isPageBottomAlmostReached && hasNextPage) {
        handler()
      }
    }

    if (!options?.enabledAction) {
      element.removeEventListener('scroll', handleElementScroll)
      return
    }

    element.addEventListener('scroll', handleElementScroll)

    return () => {
      element.removeEventListener('scroll', handleElementScroll)
    }
  }, [ref, hasNextPage, handler, options])
}
